import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getPartnerPortalDashboard } from "../../api/partnerPortalApi";
import { usePartnerAuth } from "../../auth/PartnerAuthProvider";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { partnerInventoryIssueLabel } from "../../constants/partnerInventoryHealthConstants";
import { CASAMAPA_CLIENT_VIEW_SLUG } from "../../constants/casamapaPartnerPortalConfig.js";
import { AUTOMAP_CLIENT_VIEW_SLUG } from "../../constants/automapPartnerPortalConfig.js";
import { MIDPOINT_MEDIA_CLIENT_VIEW_SLUG } from "../../constants/partnerPortalConfig.js";
import { useOptionalLocale } from "../../intl";
import {
  formatIssueProgress,
  formatPublicReadyProgress,
} from "../../utils/partnerInventoryHealthProgress";

function greetingEmoji() {
  const hour = new Date().getHours();
  if (hour < 12) return "☀️";
  if (hour < 18) return "🌤️";
  return "🌙";
}

function firstName(partner) {
  const raw = partner?.firstName || partner?.displayName || partner?.businessName || "Partner";
  return String(raw).split(" ")[0];
}

function DashboardSkeleton() {
  return (
    <div className="partner-dashboard partner-dashboard-skeleton" aria-busy="true">
      <div className="partner-skeleton-block partner-skeleton-hero" />
      <div className="partner-kpi-grid">
        {[1, 2, 3, 4, 5, 6].map((id) => (
          <div key={id} className="partner-skeleton-block partner-skeleton-kpi" />
        ))}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, hint, tone = "slate" }) {
  return (
    <article className={`partner-kpi-card partner-kpi-card--${tone}`}>
      <span className="partner-kpi-card__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="partner-kpi-card__label">{label}</span>
      <strong className="partner-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="partner-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

function FunnelRow({ label, value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="partner-funnel-row">
      <span className="partner-funnel-row__label">{label}</span>
      <div className="partner-funnel-row__track" aria-hidden="true">
        <div className="partner-funnel-row__fill" style={{ width: `${pct}%` }} />
      </div>
      <strong className="partner-funnel-row__value">{value}</strong>
    </div>
  );
}

function ProgressDelta({ progress }) {
  if (!progress?.text) return null;

  const tone = progress.tone || "neutral";
  return (
    <small className={`partner-progress-delta partner-progress-delta--${tone}`}>{progress.text}</small>
  );
}

function InventoryTrendChart({ trend = [] }) {
  if (!trend.length) {
    return <p className="muted">Daily snapshots will appear here as progress is tracked.</p>;
  }

  const maxIssues = Math.max(...trend.map((point) => point.withIssues), 1);

  return (
    <div className="partner-inventory-trend">
      <div className="partner-inventory-trend__legend">
        <span>Public-ready %</span>
        <span>Data issues</span>
      </div>
      <div className="partner-inventory-trend__bars" aria-hidden="true">
        {trend.map((point) => (
          <div key={point.date} className="partner-inventory-trend__day">
            <div className="partner-inventory-trend__pair">
              <div
                className="partner-inventory-trend__bar partner-inventory-trend__bar--ready"
                style={{ height: `${Math.max(8, point.publicReadyPct)}%` }}
                title={`${point.publicReadyPct}% public-ready`}
              />
              <div
                className="partner-inventory-trend__bar partner-inventory-trend__bar--issues"
                style={{ height: `${Math.max(8, Math.round((point.withIssues / maxIssues) * 100))}%` }}
                title={`${point.withIssues} data issues`}
              />
            </div>
            <span className="partner-inventory-trend__label">
              {String(point.date).slice(5).replace("-", "/")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PartnerPortalDashboardPage() {
  const { partner } = usePartnerAuth();
  const { path, portalLabel, clientViewSlug } = usePartnerPortalBase();
  const isCasaMapaPortal = clientViewSlug === CASAMAPA_CLIENT_VIEW_SLUG;
  const isAutoMapaAdmin = clientViewSlug === AUTOMAP_CLIENT_VIEW_SLUG;
  const isMidpointMediaPortal = clientViewSlug === MIDPOINT_MEDIA_CLIENT_VIEW_SLUG;
  const localeCtx = useOptionalLocale();
  const t = localeCtx?.t || ((key) => key);
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const inventoryHealth = data?.inventoryHealth;
  const inventoryTotals = inventoryHealth?.totals;
  const casamapaInventory = data?.casamapaInventory;
  const inventoryProgress = data?.inventoryHealthProgress;
  const inventoryTrend = data?.inventoryHealthTrend || [];
  const progressPeriodDays = inventoryProgress?.periodDays || 7;
  const publicReadyProgress = formatPublicReadyProgress(inventoryProgress, progressPeriodDays);
  const issuesProgress = formatIssueProgress(
    inventoryProgress?.withIssues,
    progressPeriodDays,
    inventoryProgress?.hasBaseline
  );

  const inventoryIssueHref = (issue, topDealerId) => {
    if (topDealerId) {
      return path(`dealers/${topDealerId}/inventory?issue=${encodeURIComponent(issue)}`);
    }
    return path("dealers");
  };

  useEffect(() => {
    let cancelled = false;

    Promise.all([getPartnerPortalDashboard()])
      .then(([dashboard]) => {
        if (cancelled) return;
        setData(dashboard);
      })
      .catch((error) => {
        if (!cancelled) setErrorMessage(error.message || "Unable to load dashboard.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const attentionItems = useMemo(() => {
    if (!data?.summary) return [];
    const s = data.summary;
    const items = [];
    if (s.newLeads > 0) {
      items.push({
        label: `${s.newLeads} new lead${s.newLeads === 1 ? "" : "s"} waiting`,
        href: path("leads"),
        action: "Review",
      });
    }
    if (!isAutoMapaAdmin && s.correctionsPending > 0) {
      items.push({
        label: `${s.correctionsPending} listing correction${s.correctionsPending === 1 ? "" : "s"} pending`,
        href: path("listing-corrections"),
        action: "Open",
      });
    }
    if (s.assetsPendingApproval > 0) {
      items.push({
        label: `${s.assetsPendingApproval} sponsorship asset${s.assetsPendingApproval === 1 ? "" : "s"} awaiting approval`,
        href: path("sponsorship-assets"),
        action: "View",
      });
    }
    if (s.profileCompleteness < 100) {
      items.push({
        label: "Complete your account profile",
        href: path("profile"),
        action: "Finish",
      });
    }
    if (isCasaMapaPortal && casamapaInventory?.totalListings > 0) {
      items.push({
        label: `${casamapaInventory.totalListings} active listings across ${casamapaInventory.agencyCount} agencies`,
        href: path("agencies"),
        action: "Browse",
      });
    }
    if (inventoryHealth?.queues?.length) {
      for (const queue of inventoryHealth.queues.slice(0, 3)) {
        items.push({
          label: `${queue.count} listings ${queue.label.toLowerCase()}`,
          href: inventoryIssueHref(queue.issue, queue.topDealerId),
          action: "Review",
        });
      }
    }
    return items;
  }, [data, inventoryHealth, path, isCasaMapaPortal, isAutoMapaAdmin, casamapaInventory]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="partner-panel">
        <h3>Something went wrong</h3>
        <p className="admin-data-health-error">{errorMessage}</p>
      </div>
    );
  }

  if (!data?.summary) {
    return null;
  }

  const s = data.summary;
  const leadMax = Math.max(s.assignedLeads, 1);
  const inventoryIssueMax = Math.max(
    ...Object.values(inventoryHealth?.issues || { fallback: 0 }),
    inventoryTotals?.withIssues || 0,
    1
  );

  return (
    <div className="partner-dashboard">
      <header className="partner-dashboard-hero">
        <div>
          <p className="partner-dashboard-hero__eyebrow">
            {greetingEmoji()} {portalLabel}
          </p>
          <h2>Welcome back, {firstName(partner)}</h2>
          <p className="partner-dashboard-hero__intro">
            {isCasaMapaPortal
              ? t("realEstate.casaMapa.admin.dashboard.subtitle")
              : isMidpointMediaPortal
                ? "Your workspace for Midpoint Media leads, sponsorship assets, and partner profile details."
                : "Your command center for dealer networks, inventory quality, and lead operations — everything in one calm, focused view."}
          </p>
        </div>
        <div className="partner-dashboard-hero__actions">
          {isCasaMapaPortal ? (
            <>
              <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("agencies")}>
                🏢 {t("realEstate.casaMapa.admin.nav.agencies")}
              </Link>
              <Link className="partner-dashboard-btn" to={path("vendors")}>
                🛠️ {t("realEstate.casaMapa.admin.nav.vendors")}
              </Link>
            </>
          ) : isMidpointMediaPortal ? (
            <Link
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              to={path("sponsorship-assets")}
            >
              ▣ Sponsorship assets
            </Link>
          ) : (
            <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("dealers")}>
              🚗 Manage dealers
            </Link>
          )}
          <Link className="partner-dashboard-btn" to={path("leads")}>
            📬 {isCasaMapaPortal ? t("realEstate.casaMapa.admin.nav.leads") : "Leads"}
          </Link>
        </div>
      </header>

      <div className="partner-quick-grid">
        {isCasaMapaPortal ? (
          <>
            <Link className="partner-quick-card" to={path("agencies")}>
              <span className="partner-quick-card__emoji">🏢</span>
              <strong>{t("realEstate.casaMapa.admin.nav.agencies")}</strong>
              <span>
                {casamapaInventory
                  ? `${casamapaInventory.agencyCount} agencies · ${casamapaInventory.totalListings.toLocaleString()} listings`
                  : t("realEstate.casaMapa.admin.dashboard.agenciesHint")}
              </span>
            </Link>
            <Link className="partner-quick-card" to={path("listings")}>
              <span className="partner-quick-card__emoji">🏘️</span>
              <strong>{t("realEstate.casaMapa.admin.nav.listings")}</strong>
              <span>{t("realEstate.casaMapa.admin.dashboard.listingsHint")}</span>
            </Link>
            <Link className="partner-quick-card" to={path("vendors")}>
              <span className="partner-quick-card__emoji">🛠️</span>
              <strong>{t("realEstate.casaMapa.admin.nav.vendors")}</strong>
              <span>
                {casamapaInventory?.vendorDirectory
                  ? `${casamapaInventory.vendorDirectory.publishedVendors} published · ${casamapaInventory.vendorDirectory.draftVendors} draft`
                  : t("realEstate.casaMapa.admin.dashboard.vendorsHint")}
              </span>
            </Link>
          </>
        ) : isMidpointMediaPortal ? (
          <Link className="partner-quick-card" to={path("profile")}>
            <span className="partner-quick-card__emoji">◉</span>
            <strong>Partner profile</strong>
            <span>
              {s.profileCompleteness < 100
                ? `${s.profileCompleteness}% complete`
                : "Account details and preferences"}
            </span>
          </Link>
        ) : (
          <Link className="partner-quick-card" to={path("dealers")}>
            <span className="partner-quick-card__emoji">🏬</span>
            <strong>Dealer network</strong>
            <span>
              {inventoryTotals
                ? `${inventoryTotals.publicReadyPct}% public-ready · ${inventoryTotals.listings.toLocaleString()} listings`
                : "Browse and edit dealer inventory"}
            </span>
          </Link>
        )}
        <Link className="partner-quick-card" to={path("leads")}>
          <span className="partner-quick-card__emoji">📥</span>
          <strong>Assigned leads</strong>
          <span>{s.newLeads} new · {s.assignedLeads} total</span>
        </Link>
        {isCasaMapaPortal ? (
        <Link className="partner-quick-card" to={path("listing-corrections")}>
          <span className="partner-quick-card__emoji">✏️</span>
          <strong>{t("realEstate.casaMapa.admin.nav.corrections")}</strong>
          <span>{s.correctionsPending} pending review</span>
        </Link>
        ) : null}
        {!isCasaMapaPortal ? (
        <Link className="partner-quick-card" to={path("sponsorship-assets")}>
          <span className="partner-quick-card__emoji">✨</span>
          <strong>Sponsorship assets</strong>
          <span>{s.assetsPendingApproval} awaiting approval</span>
        </Link>
        ) : null}
      </div>

      <div className="partner-kpi-grid">
        <KpiCard
          icon="📬"
          label="Assigned leads"
          value={s.assignedLeads}
          hint="All active assignments"
          tone="blue"
        />
        <KpiCard icon="🆕" label="New" value={s.newLeads} hint="Needs first response" tone="green" />
        <KpiCard
          icon="💬"
          label="In progress"
          value={s.contactedLeads}
          hint="Contacted or scheduled"
          tone="violet"
        />
        <KpiCard icon="✅" label="Closed" value={s.closedLeads} hint="Won or lost" tone="slate" />
        {inventoryTotals ? (
          <>
            <KpiCard
              icon="🌐"
              label="Public-ready"
              value={`${inventoryTotals.publicReadyPct}%`}
              hint={publicReadyProgress.text}
              tone="green"
            />
            <KpiCard
              icon="🛠️"
              label="Data issues"
              value={inventoryTotals.withIssues}
              hint={
                issuesProgress
                  ? issuesProgress.text
                  : `${inventoryTotals.pausedNoPhoto} paused without photos`
              }
              tone="amber"
            />
          </>
        ) : isCasaMapaPortal && casamapaInventory ? (
          <>
            <KpiCard
              icon="🏢"
              label={t("realEstate.casaMapa.admin.dashboard.agencyCount")}
              value={casamapaInventory.agencyCount}
              hint={t("realEstate.casaMapa.admin.dashboard.agenciesHint")}
              tone="blue"
            />
            <KpiCard
              icon="🏠"
              label={t("realEstate.casaMapa.admin.dashboard.totalListings")}
              value={casamapaInventory.totalListings}
              hint={`${casamapaInventory.saleListings} sale · ${casamapaInventory.rentListings} rent`}
              tone="green"
            />
            <KpiCard
              icon="🛠️"
              label={t("realEstate.casaMapa.admin.dashboard.publishedVendors")}
              value={casamapaInventory.vendorDirectory?.publishedVendors ?? 0}
              hint={t("realEstate.casaMapa.admin.dashboard.vendorDraftHint", {
                count: casamapaInventory.vendorDirectory?.draftVendors ?? 0,
              })}
              tone="amber"
            />
            <KpiCard
              icon="📥"
              label={t("realEstate.casaMapa.admin.dashboard.pendingCandidates")}
              value={casamapaInventory.vendorDirectory?.pendingCandidates ?? 0}
              tone="purple"
            />
          </>
        ) : (
          <>
            <KpiCard icon="📝" label="Corrections" value={s.correctionsPending} tone="amber" />
            <KpiCard icon="🌐" label="Client views" value={s.activeClientViews} tone="blue" />
          </>
        )}
      </div>

      {inventoryHealth && !isCasaMapaPortal ? (
        <section className="partner-panel partner-inventory-health">
          <div className="partner-panel__header">
            <div>
              <h3>Inventory data health</h3>
              <p className="muted partner-inventory-health__intro">
                Track missing photos, prices, and other gaps across your dealer network. Fix listings
                to improve AutoMapa visibility.
              </p>
            </div>
            <Link className="partner-dashboard-btn" to={path("inventory-health")}>
              Open workspace
            </Link>
          </div>

          <div className="partner-progress partner-inventory-health__progress">
            <div className="partner-progress__meta">
              <span>Public-ready listings</span>
              <div className="partner-inventory-health__progress-copy">
                <strong>{inventoryTotals.publicReadyPct}%</strong>
                <ProgressDelta progress={publicReadyProgress} />
              </div>
            </div>
            <div className="partner-progress__track" aria-hidden="true">
              <div
                className="partner-progress__fill partner-progress__fill--blue"
                style={{ width: `${Math.min(100, inventoryTotals.publicReadyPct)}%` }}
              />
            </div>
          </div>

          <div className="partner-inventory-health__trend-block">
            <div className="partner-inventory-health__trend-head">
              <h4 className="partner-inventory-health__subheading">Last {inventoryTrend.length || 7} days</h4>
              {inventoryProgress?.hasBaseline ? (
                <span className="muted">
                  Compared to snapshot from {inventoryProgress.baselineDate}
                </span>
              ) : (
                <span className="muted">Daily snapshots save automatically when you open the dashboard</span>
              )}
            </div>
            <InventoryTrendChart trend={inventoryTrend} />
          </div>

          <div className="partner-inventory-health__issues">
            {Object.entries(inventoryHealth.issues)
              .filter(([, count]) => count > 0)
              .map(([issue, count]) => {
                const queue = inventoryHealth.queues.find((entry) => entry.issue === issue);
                const issueDelta = formatIssueProgress(
                  inventoryProgress?.issues?.[issue],
                  progressPeriodDays,
                  inventoryProgress?.hasBaseline
                );
                return (
                  <div key={issue} className="partner-inventory-health__issue-row">
                    <div className="partner-inventory-health__issue-copy">
                      <FunnelRow
                        label={partnerInventoryIssueLabel(issue)}
                        value={count}
                        max={inventoryIssueMax}
                      />
                      {issueDelta ? <ProgressDelta progress={issueDelta} /> : null}
                    </div>
                    {queue?.topDealerId ? (
                      <Link
                        className="partner-inventory-health__issue-link"
                        to={inventoryIssueHref(issue, queue.topDealerId)}
                      >
                        Review
                      </Link>
                    ) : null}
                  </div>
                );
              })}
          </div>

          {inventoryHealth.topDealers?.length ? (
            <>
              <h4 className="partner-inventory-health__subheading">Dealers needing the most attention</h4>
              <ul className="partner-attention-list">
                {inventoryHealth.topDealers.slice(0, 5).map((dealer) => (
                  <li key={dealer.dealerId}>
                    <span>
                      {dealer.businessName} · {dealer.issueCount} issue
                      {dealer.issueCount === 1 ? "" : "s"} · {dealer.publicReadyPct}% public-ready
                    </span>
                    <Link to={inventoryIssueHref(dealer.topIssue, dealer.dealerId)}>Fix</Link>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {inventoryHealth.queues?.length ? (
            <div className="partner-inventory-health__queue-links">
              {inventoryHealth.queues.map((queue) => (
                <Link
                  key={queue.issue}
                  className="partner-dashboard-btn"
                  to={inventoryIssueHref(queue.issue, queue.topDealerId)}
                >
                  {queue.label} ({queue.count})
                </Link>
              ))}
            </div>
          ) : (
            <p className="muted">All tracked listings meet the current public-ready baseline.</p>
          )}
        </section>
      ) : null}

      <div className="partner-dashboard-grid">
        <section className="partner-panel">
          <h3>Lead pipeline</h3>
          <FunnelRow label="New" value={s.newLeads} max={leadMax} />
          <FunnelRow label="Contacted" value={s.contactedLeads} max={leadMax} />
          <FunnelRow label="Closed" value={s.closedLeads} max={leadMax} />
        </section>

        <section className="partner-panel">
          <div className="partner-panel__header">
            <h3>Profile health</h3>
            <span>{s.profileCompleteness}%</span>
          </div>
          <div className="partner-progress">
            <div className="partner-progress__meta">
              <span>Account profile completeness</span>
              <strong>{s.profileCompleteness}%</strong>
            </div>
            <div className="partner-progress__track" aria-hidden="true">
              <div
                className="partner-progress__fill"
                style={{ width: `${Math.min(100, s.profileCompleteness)}%` }}
              />
            </div>
          </div>
          {attentionItems.length ? (
            <>
              <h3 style={{ marginTop: "1.1rem" }}>Needs attention</h3>
              <ul className="partner-attention-list">
                {attentionItems.map((item) => (
                  <li key={item.label}>
                    <span>{item.label}</span>
                    <Link to={item.href}>{item.action}</Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="muted" style={{ marginTop: "1rem" }}>
              🎉 You&apos;re all caught up — nothing urgent right now.
            </p>
          )}
        </section>
      </div>

      {data.clientViews?.length ? (
        <section className="partner-panel">
          <div className="partner-panel__header">
            <h3>Your Client Views</h3>
            <span className="muted">{data.clientViews.length} linked</span>
          </div>
          <div className="partner-client-chip-grid">
            {data.clientViews.map((cv) => (
              <div key={cv.clientViewId} className="partner-client-chip">
                <strong>{cv.name}</strong>
                <span>{cv.relationshipType}</span>
                <span className="partner-client-chip__status">{cv.status}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
